/**
 * Coach Revenue — Track revenue estimates via client count × configurable rate.
 * Syncs to Supabase coach_settings table when coachUserId is provided;
 * falls back to AsyncStorage if the table doesn't exist yet.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase';

const RATE_KEY = 'coach_monthly_rate';
const PEAK_KEY = 'coach_peak_clients';
const DEFAULT_RATE = 150; // $/month per client

export async function getMonthlyRate(coachUserId?: string): Promise<number> {
  // Try server first
  if (coachUserId) {
    try {
      const { data } = await supabase
        .from('coach_settings')
        .select('monthly_rate')
        .eq('coach_user_id', coachUserId)
        .maybeSingle();
      if (data?.monthly_rate != null) {
        await AsyncStorage.setItem(RATE_KEY, String(data.monthly_rate));
        return data.monthly_rate;
      }
    } catch { /* fallback to AsyncStorage */ }
  }
  try {
    const val = await AsyncStorage.getItem(RATE_KEY);
    return val ? Number(val) : DEFAULT_RATE;
  } catch {
    return DEFAULT_RATE;
  }
}

export async function setMonthlyRate(rate: number, coachUserId?: string): Promise<void> {
  await AsyncStorage.setItem(RATE_KEY, String(rate));
  // Sync to server if available
  if (coachUserId) {
    try {
      await supabase
        .from('coach_settings')
        .upsert({ coach_user_id: coachUserId, monthly_rate: rate }, { onConflict: 'coach_user_id' });
    } catch { /* table may not exist yet — silently use AsyncStorage */ }
  }
}

export interface RevenueSnapshot {
  activeClients: number;
  monthlyRate: number;
  monthlyRevenue: number;
  retentionRate: number;
}

/**
 * Calculate revenue snapshot from client count and historical count.
 * retentionRate = current / peak (synced via Supabase when coachUserId provided).
 */
export async function getRevenueSnapshot(activeClients: number, coachUserId?: string): Promise<RevenueSnapshot> {
  const monthlyRate = await getMonthlyRate(coachUserId);

  // Track peak client count for retention calc — try Supabase first
  let peak = activeClients;

  if (coachUserId) {
    try {
      const { data } = await supabase
        .from('coach_settings')
        .select('peak_clients')
        .eq('coach_user_id', coachUserId)
        .maybeSingle();
      if (data?.peak_clients != null) {
        const serverPeak = Number(data.peak_clients);
        if (serverPeak > activeClients) {
          peak = serverPeak;
        } else {
          // Current exceeds server peak — update server
          peak = activeClients;
          try {
            await supabase
              .from('coach_settings')
              .upsert({ coach_user_id: coachUserId, peak_clients: activeClients }, { onConflict: 'coach_user_id' });
          } catch { /* ignore */ }
        }
        await AsyncStorage.setItem(PEAK_KEY, String(peak));
      } else {
        // No server record — fall through to AsyncStorage
        throw new Error('no server peak');
      }
    } catch {
      // AsyncStorage fallback
      try {
        const stored = await AsyncStorage.getItem(PEAK_KEY);
        if (stored) {
          const storedPeak = Number(stored);
          if (storedPeak > activeClients) {
            peak = storedPeak;
          } else {
            await AsyncStorage.setItem(PEAK_KEY, String(activeClients));
          }
        } else {
          await AsyncStorage.setItem(PEAK_KEY, String(activeClients));
        }
      } catch { /* ignore */ }
    }
  } else {
    // No coachUserId — AsyncStorage only
    try {
      const stored = await AsyncStorage.getItem(PEAK_KEY);
      if (stored) {
        const storedPeak = Number(stored);
        if (storedPeak > activeClients) {
          peak = storedPeak;
        } else {
          await AsyncStorage.setItem(PEAK_KEY, String(activeClients));
        }
      } else {
        await AsyncStorage.setItem(PEAK_KEY, String(activeClients));
      }
    } catch { /* ignore */ }
  }

  const retentionRate = peak > 0 ? Math.round((activeClients / peak) * 100) : 100;

  return {
    activeClients,
    monthlyRate,
    monthlyRevenue: activeClients * monthlyRate,
    retentionRate,
  };
}
